<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-count="$_queryHandler_numberOfQueries"
            :query-changed="$_queryHandler_hasQueryChanged"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" clearable hide-details />
            </template>

            <template #default>
              <v-row>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.department"
                    :items="departmentOptions.filter(({ value }) => ['organic', 'performance'].includes(value))"
                    label="Department"
                    clearable
                    hide-details
                    outlined
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.site_group_id"
                    :items="siteGroupOptions"
                    label="Site Group"
                    clearable
                    hide-details
                    outlined
                  />
                </v-col>
                <v-spacer />
                <v-col sm="4" class="d-flex align-center">
                  <toggle-input
                    v-model="query.params.is_archived"
                    :disabled="isLoading"
                    class="mt-0"
                    label="Show Archived Only"
                  />
                </v-col>
              </v-row>
            </template>

            <template #content>
              <data-table
                :headers="tableHeaders"
                :items="sites"
                :item-class="getTableRowClass"
                :server-items-length="pagination.total"
                :page="pagination.currentPage"
                :items-per-page="pagination.perPage"
                :footer-props="tableFooterProps"
                :loading="isLoading"
                :route="{ name: 'sites.edit', params: ['id'] }"
                fixed-width
                disable-sort
                @update:page="onPaginationclick"
                @update:items-per-page="onPerPageUpdate"
              >
                <template #item.site="{ item: site }">
                  <div class="d-inline-flex align-center">
                    <item-logo :name="site.name" :src="getSiteLogo(site)" class="ml-0 mr-2" />
                    <span>
                      <div>{{ site.name }}</div>
                    </span>
                  </div>
                </template>
                <template #item.department="{ value }">
                  <base-chip
                    dark
                    small
                    :color="value === 'organic' ? 'green' : value === 'performance' ? 'blue' : 'grey'"
                    >{{ getOptionKeyByValue(departmentOptions, value) || 'None' }}</base-chip
                  >
                </template>
                <template #item.site_group_id="{ value }">
                  <span>
                    {{ getOptionKeyByValue(siteGroupOptions, value) }}
                  </span>
                </template>
              </data-table>
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>

    <fab-create v-if="isPermittedTo('create')" type="Site" @click="showCreateModal = true" />

    <generic-modal
      v-model="showCreateModal"
      title="Create Site"
      :is-confirm-disabled="isCreateDisabled"
      @modal-cancel="onCreateModalCancel"
      @modal-confirm="onCreateModalConfirm"
    >
      <category-options
        v-for="(fields, category) in requiredCategorisedSchema.general"
        :key="category"
        :data="newSite"
        :fields="fields"
        :is-disabled="isLoading || !isPermittedTo('create')"
        :category="category"
        :show-category="Object.keys(requiredCategorisedSchema.general).length > 1"
        class="mb-6"
        @input="onInput"
      />
    </generic-modal>
  </page-loader>
</template>
<script>
// Helpers
import { mapGetters, mapMutations, mapState } from 'vuex'
import getOptionKeyByValue from '@/utils/get-option-key-by-value'
import isSet from '@/utils/is-set'
// Services
import { sites, siteGroups } from '@/services/offers'
// Mixins
import QueryParamsHandler from '@/mixins/query-params-handler'
import PermissionsHandler from '@/mixins/permissions-handler'
// Components
import BaseChip from '@/components/chips/base-chip'
import CategoryOptions from '@/components/category-options'
import DataTable from '@/components/data-table'
import FabCreate from '@/components/fab-create'
import FilterAccordion from '@/components/filter-accordion'
import GenericModal from '@/components/modals/generic-modal'
import ItemLogo from '@/components/item-logo'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'
import ToggleInput from '@/components/forms/toggle-input'

export default {
  name: 'SiteManagement',
  components: {
    BaseChip,
    CategoryOptions,
    DataTable,
    FabCreate,
    FilterAccordion,
    GenericModal,
    ItemLogo,
    PageLoader,
    SearchInput,
    SelectList,
    ToggleInput,
  },
  mixins: [QueryParamsHandler, PermissionsHandler],
  data: () => ({
    isLoading: false,
    newSite: {},
    defaultQuery: {
      is_archived: false,
    },
    queriesDefined: ['department', 'site_group_id', 'is_archived'],
    queryDefaults: ['is_archived'],
    queryTypes: { site_group_id: 'number', is_archived: 'boolean' },
    showCreateModal: false,
    siteGroupOptions: [],
    sites: [],
    tableFooterProps: {
      itemsPerPageOptions: [20, 50, 100, -1],
    },
    tableHeaders: [
      { text: 'Site', value: 'site', width: '33%' },
      { text: 'Department', value: 'department' },
      { text: 'Site Group', value: 'site_group_id' },
    ],
    trueFalseOptions: [
      { key: 'Yes', value: 1 },
      { key: 'No', value: 0 },
    ],
  }),
  computed: {
    ...mapState('sites', ['requiredCategorisedSchema', 'schema']),
    ...mapGetters({
      getItemsForOption: 'options/getItemsForOption',
    }),
    departmentOptions() {
      return this.getItemsForOption('departments')
    },
    isCreateDisabled() {
      // "promotions" is a hidden property not included in the site edit page therefore ignored here.
      return !this.schema.required.every(property => property === 'promotions' || isSet(this.newSite[property], true))
    },
    typeOptions() {
      return this.getItemsForOption('siteTypes')
    },
  },
  async created() {
    await this.getSites()
    await this.getSiteGroups()
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations({
      setPageLoading: 'general/setPageLoading',
    }),
    getOptionKeyByValue,
    async getSites() {
      this.isLoading = true
      try {
        const { perPage, currentPage: page } = this.pagination
        const {
          data,
          pagination: { total },
        } = await sites.getAllSites({
          filters: this.query.params,
          search: this.query.searchTerm,
          perPage: perPage === -1 ? null : perPage,
          page: perPage === -1 ? null : page,
        })

        this.sites = data
        this.pagination.total = total
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch sites',
        })
      }
      this.isLoading = false
    },
    async getSiteGroups() {
      this.isLoading = true
      try {
        const { data } = await siteGroups.getAll({ paginate: { perPage: null } })
        this.siteGroupOptions = data.map(({ name, id }) => ({ key: name, value: id }))
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch site groups',
        })
      }
      this.isLoading = false
    },
    getSiteLogo(site) {
      return site?.assets?.favicon
    },
    getTableRowClass() {
      return ['table-row']
    },
    handleQueryUpdate() {
      this.getSites()
    },
    onInput({ context, model, value }) {
      if (context) {
        if (this.newSite[model]) {
          this.$set(this.newSite[model], context, value)
        } else {
          this.$set(this.newSite, model, { [context]: value })
        }
      } else this.$set(this.newSite, model, value)
    },
    onCreateModalCancel() {
      this.newSite = {}
      this.showCreateModal = false
    },
    async onCreateModalConfirm() {
      this.isLoading = true
      try {
        const { id } = await sites.create(this.newSite)
        this.$notify({ type: 'success', text: 'Successfully created site' })
        this.$router.push({ name: 'sites.edit', params: { id } })
      } catch (e) {
        this.$notify({ type: 'error', text: 'Unable to create new site' })
      }
      this.isLoading = false
    },
    onPaginationclick(page) {
      this.pagination.currentPage = page
      this.getSites()
    },
    onPerPageUpdate(perPage) {
      this.pagination.perPage = perPage
      this.onPaginationclick(1)
    },
  },
}
</script>

<style lang="scss">
.table-row {
  cursor: pointer;
}
</style>
